/**
 * Default completion content for tasks without a specialized completion flow.
 * Shows task description and a confirm button with optional reason.
 * @agentic connections
 */

import { useForm } from "@mantine/form";
import { Alert, Stack, Text, Textarea } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";
import { useTeeCareTimeForTask } from "@/hooks/domain/useTeeCareTimeForEncounter";

/**
 * Props shared by all completion content components.
 * @agentic connections
 */
export type CompletionContentProps = {
  task: import("@medplum/fhirtypes").Task;
  onComplete: (
    statusReason?: string,
    completionData?: import("@benchmark/core/constants").ActivityCompletionData,
  ) => Promise<void>;
  onDone: () => void;
  onCancel: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  nextActivityLabel?: string;
  nextActivityDefaultOwner?: { ref: string; display: string };
  nextActivityAppliedRule?: import("@benchmark/core/resolvers").AppliedBusinessRule;
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  onNextTaskOwnerOverrideChange?: (
    owner: { ref: string; display: string } | null,
  ) => void;
  isResolvingNextTaskOwner?: boolean;
};

/**
 * Default confirmation form for task completion.
 * @agentic connections
 */
export const DefaultCompletionContent = ({
  task,
  onComplete,
  onCancel,
  onDirtyChange,
  nextActivityLabel,
  nextActivityDefaultOwner,
  nextActivityAppliedRule,
  nextTaskOwnerOverride,
  onNextTaskOwnerOverrideChange,
  isResolvingNextTaskOwner = false,
}: CompletionContentProps) => {
  const form = useForm({
    initialValues: {
      reason: "",
    },
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);
  const teeForTask = useTeeCareTimeForTask();
  const steps = useMemo(
    () => [
      {
        id: "details",
        title: "Details",
        subtitle: "Complete activity details",
      },
      {
        id: "assignment",
        title: "Assignment",
        subtitle: "Review next task assignment",
      },
    ],
    [],
  );

  useEffect(() => {
    onDirtyChange?.(
      !!form.values.reason || !!nextTaskOwnerOverride || !!careMinutes,
    );
  }, [form.values.reason, nextTaskOwnerOverride, careMinutes, onDirtyChange]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onComplete(form.values.reason || undefined);
      // Best-effort care-time tee — failure is logged in the hook and
      // never blocks the completion from closing.
      await teeForTask({
        task,
        minutes: careMinutes,
        method: "task-completion",
        notes: form.values.reason || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to complete task");
    } finally {
      setSubmitting(false);
    }
  }, [form.values.reason, onComplete, teeForTask, task, careMinutes]);

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={handleSubmit}
        isSubmitting={submitting}
        submitText="Complete Task"
        onCancel={onCancel}
      >
        <FormStep id="details">
          <Stack gap="md">
            <Text size="sm">
              Are you sure you want to mark this task as complete?
            </Text>

            {task.description && (
              <Text size="sm" c="dimmed">
                {task.description}
              </Text>
            )}

            <Textarea
              label="Reason (optional)"
              placeholder="Add a note about this completion..."
              {...form.getInputProps("reason")}
              autosize
              minRows={2}
              maxRows={4}
            />
          </Stack>
        </FormStep>

        <FormStep id="assignment">
          <Stack gap="md">
            <NextTaskOwnerSection
              nextActivityLabel={nextActivityLabel}
              defaultOwner={nextActivityDefaultOwner}
              appliedRule={nextActivityAppliedRule}
              overrideOwner={nextTaskOwnerOverride}
              onOverrideChange={onNextTaskOwnerOverrideChange ?? (() => {})}
              isResolving={isResolvingNextTaskOwner}
            />
            <CareTimeActivityDurationField
              minutes={careMinutes}
              onMinutesChange={setCareMinutes}
            />
          </Stack>
        </FormStep>
      </SteppedForm>
    </Stack>
  );
};
